import React, { FC, useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import dayjs from 'dayjs';
import { Button, Icon, TextInput } from '@kontentino/ui';
import { faLink } from '@fortawesome/pro-regular-svg-icons/faLink';
import { faCopy } from '@fortawesome/pro-regular-svg-icons/faCopy';
import { faCheck } from '@fortawesome/pro-regular-svg-icons/faCheck';
import { writeToClipboard } from 'app/hooks/useCopyToClipboard';

interface InviteLinkShareProps {
  link: string;
  inviteeName: string;
  expiresAt: string | null;
  initiallyCopied?: boolean;
  showEarlierLinksNote?: boolean;
  isActive?: boolean;
}

type CopyState = 'idle' | 'copied' | 'refused';

const COPIED_FEEDBACK_MS = 2500;

const getInitialCopyState = (initiallyCopied?: boolean): CopyState => {
  if (initiallyCopied === undefined) return 'idle';

  return initiallyCopied ? 'copied' : 'refused';
};

const InviteLinkShare: FC<InviteLinkShareProps> = ({
  link,
  inviteeName,
  expiresAt,
  initiallyCopied,
  showEarlierLinksNote = false,
  isActive = true,
}) => {
  const { t } = useTranslation();
  const linkFieldRef = useRef<HTMLInputElement>(null);
  const canUpdateRef = useRef(isActive);
  const [copyState, setCopyState] = useState<CopyState>(() =>
    getInitialCopyState(initiallyCopied),
  );

  const selectLink = useCallback(() => {
    linkFieldRef.current?.focus();
    linkFieldRef.current?.select();
  }, []);

  useEffect(() => {
    selectLink();
  }, [selectLink]);

  useEffect(() => {
    canUpdateRef.current = isActive;

    return () => {
      canUpdateRef.current = false;
    };
  }, [isActive]);

  useEffect(() => {
    if (copyState !== 'copied') return;

    const timeout = setTimeout(() => setCopyState('idle'), COPIED_FEEDBACK_MS);

    return () => clearTimeout(timeout);
  }, [copyState]);

  const handleCopy = useCallback(async () => {
    const isCopied = await writeToClipboard(link);

    if (!canUpdateRef.current) return;

    if (isCopied) {
      setCopyState('copied');
      return;
    }

    setCopyState('refused');
    selectLink();
  }, [link, selectLink]);

  const description = expiresAt
    ? t('inviteUserModal.shareLinkDescription', {
        name: inviteeName,
        date: dayjs(expiresAt).format('D MMM YYYY'),
      })
    : t('inviteUserModal.shareLinkDescriptionNoDate', { name: inviteeName });

  return (
    <div className="tw-mb-6 tw-rounded-lg tw-border tw-border-grayscale-30 tw-p-4">
      <div className="tw-mb-1 tw-flex tw-items-center tw-gap-2">
        <Icon icon={faLink} className="tw-text-grayscale-100" />
        <span className="tw-font-medium tw-text-grayscale-180">
          {t('inviteUserModal.shareLinkTitle')}
        </span>
      </div>
      <p className="tw-mb-3 tw-text-sm tw-text-grayscale-100">{description}</p>
      <div className="tw-flex tw-items-center tw-gap-2">
        <div className="tw-flex-1">
          <TextInput
            ref={linkFieldRef}
            readOnly
            value={link}
            aria-label={t('inviteUserModal.shareLinkTitle')}
            className="tw-w-full"
            data-cy="invite-user-modal_share-link-input"
          />
        </div>
        <Button
          variant={copyState === 'copied' ? 'success' : 'secondary'}
          className="tw-shrink-0"
          iconBefore={<Icon icon={copyState === 'copied' ? faCheck : faCopy} />}
          onClick={handleCopy}
          data-cy="invite-user-modal_share-link-copy-button"
        >
          {copyState === 'copied'
            ? t('inviteUserModal.linkCopiedShort')
            : t('copy')}
        </Button>
      </div>
      {copyState === 'refused' && (
        <p className="tw-mt-2 tw-text-sm tw-text-grayscale-100">
          {t('usersManagement.inviteLinkCopyManually')}
        </p>
      )}
      {showEarlierLinksNote && (
        <p className="tw-mt-3 tw-text-sm tw-text-grayscale-100">
          {t('usersManagement.inviteLinkEarlierLinks', { name: inviteeName })}
        </p>
      )}
    </div>
  );
};

export default InviteLinkShare;
